import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

function GiftCards() {
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showRedeem, setShowRedeem] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ value: '', purchaser_name: '', purchaser_email: '', purchaser_phone: '', recipient_name: '', recipient_email: '', expires_at: '', notes: '' });
  const [redeemForm, setRedeemForm] = useState({ code: '', amount: '' });
  const [redeemResult, setRedeemResult] = useState(null);
  const [lookupCard, setLookupCard] = useState(null);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    try {
      const [cardsRes, statsRes] = await Promise.all([
        axios.get(`/api/gift-cards?status=${filter}`),
        axios.get('/api/gift-cards/stats'),
      ]);
      if (cardsRes.data.success) setCards(cardsRes.data.giftCards);
      if (statsRes.data.success) setStats(statsRes.data.stats);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const createCard = async () => {
    if (!form.value) return alert('Enter a value');
    try {
      const res = await axios.post('/api/gift-cards', form);
      if (res.data.success) {
        setShowCreate(false);
        setForm({ value: '', purchaser_name: '', purchaser_email: '', purchaser_phone: '', recipient_name: '', recipient_email: '', expires_at: '', notes: '' });
        load();
      }
    } catch (err) { alert(err.response?.data?.error || 'Error creating card'); }
  };

  const lookupCode = async () => {
    if (!redeemForm.code) return;
    try {
      const res = await axios.get(`/api/gift-cards/lookup/${redeemForm.code}`);
      if (res.data.success) { setLookupCard(res.data.giftCard); setRedeemResult(null); }
    } catch (err) {
      setLookupCard(null);
      setRedeemResult({ error: err.response?.data?.error || 'Card not found' });
    }
  };

  const redeemCard = async () => {
    if (!redeemForm.code || !redeemForm.amount) return alert('Enter code and amount');
    try {
      const res = await axios.post('/api/gift-cards/redeem', redeemForm);
      if (res.data.success) {
        setRedeemResult({ success: true, remaining: res.data.remaining });
        setLookupCard(null);
        load();
      }
    } catch (err) {
      setRedeemResult({ error: err.response?.data?.error || 'Error redeeming' });
    }
  };

  const cancelCard = async (id) => {
    if (!window.confirm('Cancel this gift card?')) return;
    try {
      await axios.delete(`/api/gift-cards/${id}`);
      load();
    } catch (err) { alert('Error'); }
  };

  const fmt = (n) => '$' + parseFloat(n || 0).toFixed(2);

  return (
    <div style={S.container}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>🎁 Gift Cards</h1>
          <p style={S.subtitle}>Create, manage, and redeem gift cards</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={S.btnOutline} onClick={() => { setShowRedeem(true); setShowCreate(false); }}>🔍 Redeem</button>
          <button style={S.btnPrimary} onClick={() => { setShowCreate(true); setShowRedeem(false); }}>+ Create Gift Card</button>
        </div>
      </div>

      {/* Stats */}
      <div style={S.statsRow}>
        {[
          { label: 'Total Sold', value: fmt(stats.totalSold), icon: '💰', color: '#ff6b2b' },
          { label: 'Active Cards', value: stats.active || 0, icon: '🎴', color: '#3b82f6' },
          { label: 'Outstanding Balance', value: fmt(stats.totalBalance), icon: '💳', color: '#10b981' },
          { label: 'Total Redeemed', value: fmt(stats.totalRedeemed), icon: '✅', color: '#8b5cf6' },
        ].map((s, i) => (
          <div key={i} style={S.statCard}>
            <div style={{ ...S.statIcon, background: s.color + '15', color: s.color }}>{s.icon}</div>
            <div style={S.statValue}>{s.value}</div>
            <div style={S.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={S.cardTitle}>Create Gift Card</h2>
            <button style={S.closeBtn} onClick={() => setShowCreate(false)}>✕</button>
          </div>
          <div style={S.formGrid}>
            <div style={S.fieldGroup}>
              <label style={S.label}>Value *</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: 10, color: '#888', fontWeight: 600 }}>$</span>
                <input type="number" style={{ ...S.input, paddingLeft: 28 }} value={form.value}
                  onChange={e => setForm({ ...form, value: e.target.value })} placeholder="100.00" />
              </div>
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>Expires</label>
              <input type="date" style={S.input} value={form.expires_at}
                onChange={e => setForm({ ...form, expires_at: e.target.value })} />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>Purchaser Name</label>
              <input style={S.input} value={form.purchaser_name}
                onChange={e => setForm({ ...form, purchaser_name: e.target.value })} />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>Purchaser Phone</label>
              <input style={S.input} value={form.purchaser_phone}
                onChange={e => setForm({ ...form, purchaser_phone: e.target.value })} />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>Recipient Name</label>
              <input style={S.input} value={form.recipient_name}
                onChange={e => setForm({ ...form, recipient_name: e.target.value })} />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>Recipient Email</label>
              <input style={S.input} value={form.recipient_email}
                onChange={e => setForm({ ...form, recipient_email: e.target.value })} />
            </div>
            <div style={{ ...S.fieldGroup, gridColumn: '1 / -1' }}>
              <label style={S.label}>Notes</label>
              <input style={S.input} value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" />
            </div>
          </div>
          {/* Quick amounts */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[25, 50, 100, 150, 200, 500].map(a => (
              <button key={a} onClick={() => setForm({ ...form, value: a })}
                style={{ ...S.quickAmt, ...(parseFloat(form.value) === a ? { background: '#ff6b2b', color: '#fff' } : {}) }}>
                ${a}
              </button>
            ))}
          </div>
          <button style={S.btnPrimary} onClick={createCard}>🎁 Create Gift Card</button>
        </div>
      )}

      {/* Redeem Modal */}
      {showRedeem && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={S.cardTitle}>Redeem Gift Card</h2>
            <button style={S.closeBtn} onClick={() => { setShowRedeem(false); setLookupCard(null); setRedeemResult(null); }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <input style={{ ...S.input, flex: 1, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700 }}
              placeholder="XXXX-XXXX-XXXX" value={redeemForm.code}
              onChange={e => setRedeemForm({ ...redeemForm, code: e.target.value })} />
            <button style={S.btnOutline} onClick={lookupCode}>Look Up</button>
          </div>
          {lookupCard && (
            <div style={{ ...S.lookupResult, borderColor: '#10b981' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>{lookupCard.code}</div>
                  <div style={{ color: '#888', fontSize: 13 }}>
                    {lookupCard.recipient_name || lookupCard.purchaser_name || 'No name'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>{fmt(lookupCard.balance)}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>of {fmt(lookupCard.initial_value)}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <span style={{ position: 'absolute', left: 12, top: 10, color: '#888' }}>$</span>
                  <input type="number" style={{ ...S.input, paddingLeft: 28 }} placeholder="Amount to redeem"
                    value={redeemForm.amount} max={lookupCard.balance}
                    onChange={e => setRedeemForm({ ...redeemForm, amount: e.target.value })} />
                </div>
                <button style={{ ...S.btnPrimary, background: '#10b981' }} onClick={redeemCard}>Redeem</button>
              </div>
            </div>
          )}
          {redeemResult?.success && (
            <div style={{ ...S.lookupResult, borderColor: '#10b981', background: '#ecfdf5' }}>
              ✅ Redeemed! Remaining balance: <strong>{fmt(redeemResult.remaining)}</strong>
            </div>
          )}
          {redeemResult?.error && (
            <div style={{ ...S.lookupResult, borderColor: '#ef4444', background: '#fef2f2', color: '#dc2626' }}>
              ❌ {redeemResult.error}
            </div>
          )}
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['all', 'active', 'redeemed', 'expired', 'cancelled'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ ...S.filterBtn, ...(filter === f ? { background: '#ff6b2b', color: '#fff' } : {}) }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Cards list */}
      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {cards.length === 0 && (
            <div style={S.empty}>No gift cards {filter !== 'all' ? `with status "${filter}"` : 'yet'}. Create one above!</div>
          )}
          {cards.map(card => (
            <div key={card.id} style={S.cardRow}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16, letterSpacing: 1 }}>{card.code}</span>
                  <span style={{ ...S.statusBadge, background: statusColor(card.status) + '15', color: statusColor(card.status) }}>
                    {card.status}
                  </span>
                </div>
                <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
                  {card.purchaser_name && `From: ${card.purchaser_name}`}
                  {card.recipient_name && ` → To: ${card.recipient_name}`}
                  {card.expires_at && ` · Expires: ${new Date(card.expires_at).toLocaleDateString()}`}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: card.balance > 0 ? '#10b981' : '#999' }}>
                  {fmt(card.balance)}
                </div>
                <div style={{ fontSize: 12, color: '#999' }}>of {fmt(card.initial_value)}</div>
              </div>
              {card.status === 'active' && (
                <button style={S.cancelBtn} onClick={() => cancelCard(card.id)} title="Cancel">✕</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function statusColor(s) {
  return { active: '#10b981', redeemed: '#3b82f6', expired: '#f59e0b', cancelled: '#ef4444' }[s] || '#888';
}

const S = {
  container: { padding: 24, maxWidth: 900, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 28, fontWeight: 800, color: '#252840', margin: 0 },
  subtitle: { color: '#888', marginTop: 4, fontSize: 14 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 },
  statCard: { background: '#12121f', borderRadius: 14, padding: 18, border: '1px solid #1e1e35', textAlign: 'center' },
  statIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 18 },
  statValue: { fontSize: 22, fontWeight: 800, color: '#e2e2e8' },
  statLabel: { fontSize: 12, color: '#666', marginTop: 2 },
  card: { background: '#12121f', borderRadius: 16, padding: 24, border: '1px solid #1e1e35', marginBottom: 20 },
  cardTitle: { fontSize: 20, fontWeight: 700, color: '#e2e2e8', margin: 0 },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999', padding: '4px 8px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 },
  fieldGroup: {},
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#aaa', marginBottom: 6 },
  input: { width: '100%', padding: '10px 14px', border: '1.5px solid #1e1e35', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', color: '#e2e2e8', backgroundColor: '#1a1c2e' },
  quickAmt: { padding: '8px 16px', border: '2px solid #1e1e35', borderRadius: 8, background: '#1a1c2e', cursor: 'pointer', fontWeight: 700, fontSize: 14, color: '#e2e2e8', transition: 'all 0.2s' },
  btnPrimary: { padding: '12px 24px', background: 'linear-gradient(135deg, #ff6b2b, #ff8f40)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  btnOutline: { padding: '12px 24px', background: 'transparent', color: '#ff6b2b', border: '2px solid #ff6b2b', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  lookupResult: { border: '2px solid #e0e0e0', borderRadius: 12, padding: 16, marginTop: 12 },
  filterBtn: { padding: '8px 16px', border: '1.5px solid #1e1e35', borderRadius: 8, background: '#1a1c2e', cursor: 'pointer', color: '#e2e2e8', fontWeight: 600, fontSize: 13, transition: 'all 0.2s' },
  cardRow: { display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: '#16162a', borderRadius: 12, border: '1px solid #1e1e35', transition: 'box-shadow 0.2s' },
  statusBadge: { padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' },
  cancelBtn: { background: 'none', border: 'none', color: '#ccc', fontSize: 16, cursor: 'pointer', padding: '4px 8px' },
  empty: { textAlign: 'center', padding: 40, color: '#666', background: '#12121f', borderRadius: 12 },
};

export default GiftCards;
